import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function PupilDecisionSupport({ lessonId }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("Help me choose a goal and challenge for my next lesson using my learning so far.");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(null);
  const previous = useRef(null);

  useEffect(() => () => pending.current?.abort(), []);

  const ask = async () => {
    if (pending.current || !question.trim()) return;
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/run-my-day/pupil/lessons/${encodeURIComponent(lessonId)}/preparation/support`,
        { question: question.trim(), ...(previous.current ? { previous_exchange: previous.current } : {}) },
        { withCredentials: true, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (data?.lesson_id !== lessonId || !data?.answer || data.choices_changed !== false) throw new Error("Unconfirmed response");
      setResult(data);
      previous.current = { question: question.trim(), answer: data.answer.slice(0, 4000) };
    } catch {
      if (!controller.signal.aborted) setError("Karen couldn’t load your support. You can try again or continue choosing your own answers.");
    } finally {
      if (!controller.signal.aborted) setBusy(false);
      if (pending.current === controller) pending.current = null;
    }
  };

  return <aside className="rounded-lg border border-blue-200 p-3 space-y-2">
    <button type="button" className="min-h-11 font-semibold text-blue-700 dark:text-blue-300"
      aria-expanded={open} onClick={() => setOpen(!open)}>
      {open ? "Hide Karen’s support" : "Ask Karen for support"}
    </button>
    {open && <>
      <p>You choose. Karen can help you explore next steps using your recorded lesson outcomes and homework feedback. Your answers stay yours to edit and save.</p>
      <label className="block">What would you like help deciding?
        <textarea value={question} onChange={(event) => setQuestion(event.target.value)} maxLength={1200} rows={3}
          className="mt-2 w-full rounded border p-3 text-base text-slate-900 bg-white" />
      </label>
      <button type="button" disabled={busy || !question.trim()} onClick={ask}
        className="min-h-11 rounded bg-blue-700 px-4 text-white disabled:opacity-50">{busy ? "Thinking…" : "Ask Karen"}</button>
      {error && <p role="alert">{error}</p>}
      {result && <div role="status" className="space-y-2">
        {!result.ai_available && !result.conversation_paused && <p>AI conversation is unavailable just now. You can pause or choose your own answers.</p>}
        <p className="whitespace-pre-wrap">{result.answer}</p>
        {result.record_summary && <details>
          <summary>Shared learning details</summary>
          <p>{result.record_summary}</p>
        </details>}
        {!result.conversation_paused && <p className="text-xs">Your choices stay yours. Evidence can suggest a goal; your instructor can confirm what you have demonstrated.</p>}
      </div>}
    </>}
  </aside>;
}

